import React from 'react';

import { Cancel, Search } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import { Autocomplete, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import {
    DateContainer,
    DateRangeContainer,
    FilterCount,
    InputContainer,
    Label,
    Parent,
    StyledTextField
} from './styles';
import ClassesContainer from '../ClassesContainer';
import { Option } from '../types';

interface ClassFiltersInterface {
    setPage: React.Dispatch<React.SetStateAction<number>>;
}

const ClassFilters: React.FC<ClassFiltersInterface> = ({ setPage }) => {
    const { t } = useTranslation('portal');

    const {
        instructors,
        filteredClasses,
        selectedInstructor,
        setSelectedInstructor,
        searchValue,
        setSearchValue,
        startDate,
        setStartDate,
        endDate,
        setEndDate
    } = useContainer(ClassesContainer);

    const clearAllFilters = () => {
        setPage(1);
        setSearchValue('');
        setSelectedInstructor(null);
        setStartDate(null);
        setEndDate(null);
    };

    const onSearchChange = (value: string) => {
        setPage(1);
        setSearchValue(value);
    };

    const onStartDateChange = (date: Dayjs | null) => {
        setPage(1);
        setStartDate(date);
    };

    const onEndDateChange = (date: Dayjs | null) => {
        setPage(1);
        setEndDate(date);
    };

    const options: Option[] = instructors.map((instructor) => {
        const { user_name: username, email } = instructor;

        return {
            email,
            label: username
        };
    });

    const returnFilterCount = () => {
        const hasFilters = searchValue !== '' || selectedInstructor || startDate || endDate;

        if (hasFilters) {
            const returnText = (count: number): string => {
                switch (count) {
                    case 0:
                        return t('classes-page.no-classes-found');
                    case 1:
                        return t('classes-page.showing-1-results');
                    default:
                        return t('classes-page.showing-results-with-count', { count });
                }
            };

            return <FilterCount>{returnText(filteredClasses.length)}</FilterCount>;
        }

        return null;
    };

    return (
        <>
            <Parent>
                <InputContainer>
                    <Label htmlFor="search">{t('search-placeholder')}</Label>
                    <StyledTextField
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        sx={{ width: 375 }}
                        size="small"
                        id="search"
                        name="search"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search />
                                </InputAdornment>
                            )
                        }}
                    />
                </InputContainer>

                <InputContainer>
                    <Label htmlFor="instructor">{t('classes-page.instructor')}</Label>
                    <Autocomplete
                        value={selectedInstructor}
                        onChange={(_, newValue) => {
                            setPage(1);
                            setSelectedInstructor(newValue);
                        }}
                        isOptionEqualToValue={(option, val) => option.email === val.email}
                        id="controllable-states-demo"
                        options={options}
                        size="small"
                        sx={{ width: 250 }}
                        renderInput={(params) => (
                            <StyledTextField {...params} placeholder={t('classes-page.all-instructor')} />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.email}>
                                {option.label}
                            </li>
                        )}
                    />
                </InputContainer>

                <DateRangeContainer>
                    <DateContainer>
                        <Label htmlFor="from">{t('classes-page.from')}</Label>
                        <DatePicker
                            value={startDate}
                            onChange={(date: Dayjs | null) => onStartDateChange(date)}
                            renderInput={(params) => (
                                <TextField size="small" sx={{ width: 200 }} id="from" name="from" {...params} />
                            )}
                        />
                    </DateContainer>
                    <DateContainer>
                        <Label htmlFor="to">{t('classes-page.to')}</Label>
                        <DatePicker
                            value={endDate}
                            onChange={(date: Dayjs | null) => onEndDateChange(date)}
                            renderInput={(params) => (
                                <TextField size="small" sx={{ width: 200 }} id="to" name="to" {...params} />
                            )}
                        />
                    </DateContainer>
                    <DateContainer>
                        <Tooltip arrow title={t('classes-page.clear-selection')}>
                            <IconButton onClick={clearAllFilters}>
                                <Cancel />
                            </IconButton>
                        </Tooltip>
                    </DateContainer>
                </DateRangeContainer>
            </Parent>
            {returnFilterCount()}
        </>
    );
};

export default ClassFilters;
